/**
 * Helpers for the two admin-web pages of the MCP OAuth flow (`/oauth/login`,
 * `/oauth/consent`). The authorization server is better-auth in admin-api
 * (`packages/auth/src/mcp-oauth.ts`); it redirects the browser here with a
 * SIGNED copy of the authorize request in the query string, and expects that
 * signed query back as `oauth_query` on sign-in / consent so it can resume the
 * authorize request server-side. Nothing here makes a security decision — the
 * server re-verifies the signature and the admin's access.
 */

/** Which OAuth page (if any) a pathname belongs to. Matched outside the app router. */
export function oauthPageFor(pathname: string): 'login' | 'consent' | null {
  const p = pathname.replace(/\/+$/, '');
  if (p === '/oauth/login') return 'login';
  if (p === '/oauth/consent') return 'consent';
  return null;
}

const SIGNED_PARAM_NAMES = 'ba_param';

/**
 * The signed part of the page's query string, in the exact form the server
 * verifies — a mirror of `buildSignedOAuthQuery` from
 * `@better-auth/oauth-provider/client` (kept local so admin-web does not pull
 * the better-auth client runtime for one function). Keeps `sig`, the
 * `ba_param` list and every parameter that list names; drops anything else.
 * `null` when the page was not reached through a signed redirect.
 */
export function signedOAuthQuery(search: string): string | null {
  const params = new URLSearchParams(search);
  if (!params.has('sig')) return null;
  const signedNames = new Set(params.getAll(SIGNED_PARAM_NAMES));
  if (signedNames.size === 0) return null;
  const out = new URLSearchParams();
  for (const [key, value] of params.entries()) {
    if (key === 'sig' || key === SIGNED_PARAM_NAMES || signedNames.has(key)) out.append(key, value);
  }
  return out.toString();
}

/**
 * Where to send the browser after sign-in / consent. better-auth answers a
 * fetch with `{ redirect: true, url }` (or `{ redirect_uri }` from consent);
 * `null` means the response carried no OAuth continuation.
 */
export function oauthRedirectTarget(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as { redirect?: unknown; url?: unknown; redirect_uri?: unknown };
  if (b.redirect === true && typeof b.url === 'string' && b.url) return b.url;
  if (typeof b.redirect_uri === 'string' && b.redirect_uri) return b.redirect_uri;
  return null;
}

/** One consent line: what the scope lets the AI do, plus an optional highlighted warning. */
export interface ScopeLine {
  scope: string;
  label: string;
  /** Shown as a warning plaque right under the line (e.g. «тратит реальные деньги»). */
  warning?: string;
}

const SCOPE_LINES: Record<string, Omit<ScopeLine, 'scope'>> = {
  // «Эти данные — только чтение» (not «менять ничего не сможет»): next to mcp:ads the
  // old wording read as «the AI can change nothing at all», which is false.
  'mcp:read': {
    label:
      'Читать данные рекламы и окупаемости: кампании, настройки, статистику, прогнозы модели и ' +
      'факты по покупкам. Эти данные — только чтение.',
  },
  // docs/agent-data-mcp.md → «Реклама: инструменты launch_*». Telegram marks only
  // activation/pause «(через нейросеть)», so the line says exactly that.
  'mcp:ads': {
    label:
      'Работать с разделом «Реклама → Запуски FB» от вашего имени и с вашими правами: смотреть ' +
      'кабинеты, шаблоны, медиатеку и запуски; создавать и менять шаблоны и черновики, загружать ' +
      'медиа; выполнять, включать, ставить на паузу и откатывать запуски. Каждое действие ' +
      'записывается в журнал админки, а включение и пауза в Telegram помечаются «через нейросеть».',
    warning: 'Это тратит реальные деньги в рекламном кабинете Meta.',
  },
  openid: { label: 'Узнать, под каким аккаунтом вы вошли.' },
  profile: { label: 'Видеть ваше имя.' },
  email: { label: 'Видеть ваш email.' },
  offline_access: {
    label:
      'Оставаться подключённым: обновлять доступ без повторного входа (до 30 дней с последнего обновления).',
  },
};

/** Text for a scope this page has no description for — never the bare scope code. */
export const UNKNOWN_SCOPE_LABEL =
  'Дополнительное разрешение без описания. Если не знаете, зачем оно нужно, — отклоните подключение.';

/**
 * The info notice on the consent page. First sentence = spec D11 (answers go
 * to the AI provider). The rest states how access really ends: the provider
 * keeps refreshing tokens (rotated, up to 30 days) until an owner disables
 * the admin or takes the role away in the support-bot admin panel —
 * disconnecting the connector in the AI app only stops that one app. The
 * `launch_*` (mcp:ads) answers carry no buyer data either, but do carry ad
 * texts, account/launch names and the email of the admin who created a batch.
 */
export const CONSENT_NOTICE =
  'Ответы на запросы нейросети уходят её провайдеру (Anthropic, OpenAI и т.п.). Данных ' +
  'покупателей (email, имена, карты) в них нет; в ответах по рекламе есть тексты объявлений, ' +
  'названия кабинетов и запусков и email сотрудников, которые их создали. Доступ обновляется ' +
  'сам до 30 дней с последнего обновления. Полностью его прекращает владелец: отключит ваш ' +
  'аккаунт или уберёт роль с доступом к данным в админке support-bot — так же снимается и ' +
  'доступ к рекламе. Отключение коннектора в приложении нейросети останавливает только это ' +
  'приложение.';

/**
 * Human-readable lines for a space-separated `scope`, in request order. An
 * unknown scope gets a safe generic text (and a dev-console warning), never
 * its bare code.
 */
export function describeScopes(scope: string | null): ScopeLine[] {
  return (scope ?? '')
    .split(' ')
    .filter(Boolean)
    .map((s) => {
      const known = SCOPE_LINES[s];
      if (known) return { scope: s, ...known };
      if (import.meta.env.DEV) {
        console.warn(`[oauth consent] no description for scope "${s}" — add it to SCOPE_LINES`);
      }
      return { scope: s, label: UNKNOWN_SCOPE_LABEL };
    });
}

/**
 * Which MCP connector (module, spec builder-v4 §16.2 / §16.4 п. 5) the
 * consent is for — read from the RFC 8707 `resource` of the signed authorize
 * request by its path, so admin-web needs no copy of the resource URLs.
 * `null` for a request without a resource or an unknown path (the page then
 * says nothing about the connector, as before modules).
 */
export interface McpConnector {
  module: 'analytics' | 'launch';
  /** The connector's name as users add it in the AI app. */
  name: string;
  /** One plain sentence: what this connector is for. */
  summary: string;
  /** Money warning, shown once for the whole connector. */
  warning?: string;
}

const MCP_CONNECTORS: Record<string, McpConnector> = {
  '/mcp': {
    module: 'analytics',
    name: 'WIT LAB · Аналитика',
    summary: 'данные рекламы и окупаемости: статистика, прогнозы, покупки.',
  },
  '/mcp/launch': {
    module: 'launch',
    name: 'WIT LAB · FB-запуски',
    summary: 'раздел «Реклама → Запуски FB»: шаблоны, медиатека и запуски рекламы.',
    warning:
      'Через этот коннектор нейросеть сможет создавать, выполнять и включать запуски рекламы ' +
      'от вашего имени. Это тратит реальные деньги в рекламном кабинете Meta.',
  },
};

/** Consent is stored per connector: connecting the other one asks again. */
export const CONNECTOR_CONSENT_NOTE =
  'Разрешение даётся отдельно на каждый коннектор: при подключении другого коннектора WIT LAB ' +
  'страница появится снова.';

export function mcpConnectorFor(resource: string | null): McpConnector | null {
  if (!resource) return null;
  let path: string;
  try {
    path = new URL(resource).pathname.replace(/\/+$/, '');
  } catch {
    return null;
  }
  return MCP_CONNECTORS[path] ?? null;
}

/**
 * Every connector the request names (RFC 8707 allows several `resource`). The
 * authorization server refuses more than one MCP resource per request; if a
 * request with several ever got here, the page names them all and keeps the
 * money warning when any of them carries one.
 */
export function mcpConnectorsFor(resources: readonly string[]): McpConnector[] {
  const out: McpConnector[] = [];
  for (const r of resources) {
    const c = mcpConnectorFor(r);
    if (c && !out.some((x) => x.module === c.module)) out.push(c);
  }
  return out;
}
